import React, { useState } from "react"
import PropTypes from "prop-types"

import { upgradeProPlus } from "./api"
import classNames from "classnames"

import Modal from "shared/components/modal"
import "./modal.scss"

const UpgradeToProModal = ({
  appletFeatureFlags,
  appletQuotaTotal,
  appletsInUse,
  confirming: defaultConfirming,
  currentTier,
  eligibleForTrial,
  isTrialing,
  newProPlusSubscriptionUrl,
  newProSubscriptionUrl,
  onClose,
  onUpgrade,
  proPlusProductId,
  step,
  subscriptionPaymentType,
  visible,
}) => {
  const upgradingExistingSubscription = currentTier !== "free"
  const [confirmingUpgrade, setConfirmingUpgrade] = useState(defaultConfirming)
  const [loading, setLoading] = useState(false)

  let copy
  let title = "Get Pro+"
  let url = newProPlusSubscriptionUrl
  let mainCTALabel = eligibleForTrial ? "Start 7-day trial" : "Upgrade"

  if (upgradingExistingSubscription) {
    mainCTALabel = "Upgrade to Pro+"
  }

  switch (step) {
    case "applet_quota":
      if (currentTier === "intermediate_pro") {
        copy = `You're using ${appletsInUse} of your ${appletQuotaTotal} Applets
        limit. Upgrade now for unlimited Applets and get access to queries, filter code, and multiple accounts.`
      } else {
        // free
        title = "Get IFTTT Pro"
        copy = `You're using ${appletsInUse} of your ${appletQuotaTotal} Applets
        limit. Upgrade now to get more Applets, faster speeds, and multiple actions.`
        url = newProSubscriptionUrl
      }
      break
    case "default":
    case "queries":
    case "filter_code":
      copy =
        "Upgrade to get access to queries, filter code, and multiple accounts."

      if (appletFeatureFlags.pro_features) {
        copy =
          "This Applet uses Pro+ features. Upgrade to get access to queries, filter code, and multiple accounts."
      }
      break
    case "plan_upgrade":
      copy =
        "Upgrade to get access to queries, filter code, and multiple accounts."
      break
    case "multi_service_account":
      copy = "Upgrade to get access to multiple accounts."
      break
    case "multi_action":
      title = "Get IFTTT Pro"
      copy =
        "Upgrade to get access to 20 Applets, faster speeds, and multiple actions."
      url = newProSubscriptionUrl

      if (appletFeatureFlags.intermediate_pro_features) {
        copy =
          "This Applet uses Pro features. Upgrade to get access to 20 Applets, faster speeds, and multiple actions."
      } else if (appletFeatureFlags.pro_features) {
        title = "Get Pro+"
        copy =
          "This Applet uses Pro+ features. Upgrade to get access to queries, filter code, and multiple accounts."
        url = newProPlusSubscriptionUrl
      }
      break
    default:
      throw new Error(`Unknown step: ${step}`)
  }

  const trackData = JSON.stringify({
    object_type: "upgrade_to_pro_modal",
    object_id: `${step}_intercept`,
    location_type: "diy_composer",
  })

  const upgradeTrackData = JSON.stringify({
    object_type: "upgrade_to_pro_modal",
    object_id: `${step}_intercept_upgrade`,
    location_type: "diy_composer",
  })

  const handleProUpgrade = async ({ productId }) => {
    setLoading(true)
    const { errors, user_subscription } = await upgradeProPlus({
      productId,
    })
    setLoading(false)
    if (!errors?.length && user_subscription.plan_id == "pro") {
      window.appendFlash("Upgrade complete. Welcome to Pro+!", "success")
      onUpgrade?.()
    } else {
      const errorMessage =
        errors[0]?.message ||
        "There was a problem upgrading your account. Please contact support for more information."
      window.appendFlash(errorMessage, "danger")
    }
    onClose()
  }

  return (
    <div styleName="modal-container">
      <Modal show={visible} closeListener={onClose} includeCloseX={false}>
        {confirmingUpgrade ||
        (upgradingExistingSubscription &&
          subscriptionPaymentType === "stripe") ? (
          <UpgradeConfirmation
            canUpgrade={subscriptionPaymentType === "stripe"}
            eligibleForTrial={eligibleForTrial}
            handleProUpgrade={handleProUpgrade}
            loading={loading}
            onClose={onClose}
            proPlusProductId={proPlusProductId}
            trackData={upgradeTrackData}
            isTrialing={isTrialing}
          />
        ) : (
          <>
            <h3>{title}</h3>
            <p>{copy}</p>
            <div className="action-buttons-container">
              <a
                role="button"
                className="button-tertiary"
                href={upgradingExistingSubscription ? null : url}
                onClick={() => {
                  if (upgradingExistingSubscription) {
                    setConfirmingUpgrade(true)
                  }
                }}
                data-track-ifttt-next-event="click"
                data-track-data={trackData}
              >
                {mainCTALabel}
              </a>
              <button
                className="button-tertiary button-cancel-link"
                onClick={onClose}
              >
                Maybe later
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}

UpgradeToProModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  currentTier: PropTypes.oneOf([
    "free",
    "intermediate_pro",
    "legacy_pro",
    "pro",
  ]).isRequired,
  step: PropTypes.oneOf([
    "applet_quota",
    "queries",
    "filter_code",
    "multi_action",
    "multi_service_account",
    "plan_upgrade",
    "default",
  ]).isRequired,
  newProPlusSubscriptionUrl: PropTypes.string.isRequired,
  newProSubscriptionUrl: PropTypes.string,
  onClose: PropTypes.func,
  onUpgrade: PropTypes.func,
  appletsInUse: PropTypes.number,
  appletQuotaTotal: PropTypes.number,
  eligibleForTrial: PropTypes.bool.isRequired,
  proPlusProductId: PropTypes.string.isRequired,
  subscriptionPaymentType: PropTypes.string,
  confirming: PropTypes.bool,
  appletFeatureFlags: PropTypes.objectOf(PropTypes.bool),
  isTrialing: PropTypes.bool,
}

const UpgradeConfirmation = ({
  canUpgrade,
  handleProUpgrade,
  loading,
  onClose,
  proPlusProductId,
  trackData,
  isTrialing,
}) => {
  let mainBody = canUpgrade
    ? isTrialing
      ? "Continue your trial with Pro+ to get access to queries, filter code, and multiple accounts."
      : "We'll bill you now, minus a credit for any remaining days on your current plan."
    : `Your mobile subscription can't be updated on the web. You can upgrade your plan using the IFTTT app.`

  let mainTitle = canUpgrade ? "Try Pro+" : "Upgrade with the IFTTT app"

  return (
    <>
      <h3>{mainTitle}</h3>
      <p>{mainBody}</p>
      <div className="action-buttons-container">
        {canUpgrade && (
          <button
            className="button-tertiary"
            disabled={loading}
            onClick={() => handleProUpgrade({ productId: proPlusProductId })}
            data-track-ifttt-next-event="click"
            data-track-data={trackData}
          >
            {loading ? "Upgrading..." : "Upgrade"}
          </button>
        )}
        <button
          className={classNames("button-tertiary", {
            "button-cancel-link": canUpgrade,
          })}
          onClick={onClose}
        >
          {canUpgrade ? "No thanks" : "Close"}
        </button>
      </div>
    </>
  )
}

UpgradeConfirmation.propTypes = {
  canUpgrade: PropTypes.bool,
  handleProUpgrade: PropTypes.func.isRequired,
  isTrialing: PropTypes.bool,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  proPlusProductId: PropTypes.string.isRequired,
  trackData: PropTypes.string.isRequired,
}

export default UpgradeToProModal
